import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getVourcherDetail } from "../../api/Vourcher";
import { VoucherRule, VoucherTemplateItemV2 } from "../../api/VourcherType";

interface GuidelineModalProps {
    open: boolean;
    onClose: () => void;
    voucherId?: number | string | null;
}

function parseRuleValue(value: VoucherRule["rule_value"]): string {
    if (Array.isArray(value)) {
        return value.map((item) => String(item)).join(", ");
    }
    if (value === null || value === undefined) return "";
    return String(value);
}

function getRuleStatusLabel(isEligible?: boolean | null): string {
    if (isEligible === true) return "Đã đạt";
    if (isEligible === false) return "Chưa đạt";
    return "Chưa xác định";
}

function extractPointExchange(voucher: VoucherTemplateItemV2 | null): string {
    if (!voucher) return "--";

    const pointRule = voucher.rules?.find((rule) =>
        ["point", "point_cost", "point_required", "redeem_point", "loyalty_point"].includes(rule.rule_type)
    );

    if (!pointRule) return "--";

    const raw = Array.isArray(pointRule.rule_value) ? pointRule.rule_value[0] : pointRule.rule_value;
    const points = Number(raw);
    if (!Number.isFinite(points)) return parseRuleValue(pointRule.rule_value) || "--";

    return `${points.toLocaleString("vi-VN")} điểm`;
}

function extractVoucherDetailText(voucher: VoucherTemplateItemV2 | null): string {
    if (!voucher) return "Không có dữ liệu.";

    const value = Number(voucher.value || 0);
    const minOrderRule = voucher.rules?.find((rule) => rule.rule_type === "min_order_amount");
    const minOrderRaw = minOrderRule
        ? (Array.isArray(minOrderRule.rule_value) ? minOrderRule.rule_value[0] : minOrderRule.rule_value)
        : null;
    const minOrder = Number(minOrderRaw);

    if (voucher.type === "percent") {
        if (Number.isFinite(minOrder)) {
            return `Giảm ${value}% cho đơn từ ${minOrder.toLocaleString("vi-VN")}đ`;
        }
        return `Giảm ${value}%`;
    }

    if (voucher.type === "ship") {
        return "Ưu đãi phí vận chuyển";
    }

    if (voucher.type === "gift") {
        return "Ưu đãi quà tặng";
    }

    if (Number.isFinite(minOrder)) {
        return `Tặng voucher ${value.toLocaleString("vi-VN")}đ cho đơn từ ${minOrder.toLocaleString("vi-VN")}đ`;
    }

    return `Giảm ${value.toLocaleString("vi-VN")}đ`;
}

const GuidelineModal: React.FC<GuidelineModalProps> = ({ open, onClose, voucherId }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [detail, setDetail] = useState<VoucherTemplateItemV2 | null>(null);

    useEffect(() => {
        const loadDetail = async () => {
            if (!open || !voucherId) {
                setDetail(null);
                return;
            }

            setLoading(true);
            const res = await getVourcherDetail(voucherId);
            if (res.status && res.data) {
                setDetail(res.data);
            } else {
                setDetail(null);
            }
            setLoading(false);
        };

        loadDetail();
    }, [open, voucherId]);

    const conditionLines = useMemo(() => {
        if (!detail?.rules?.length) {
            return ["Vui lòng kiểm tra điều kiện khi áp dụng mã."];
        }

        const lines = detail.rules
            .map((rule) => {
                if (rule.rule_description) return rule.rule_description;
                const valueText = parseRuleValue(rule.rule_value);
                if (!valueText) return "";
                return `${rule.rule_type}: ${valueText}`;
            })
            .filter(Boolean);

        return lines.length > 0 ? lines : ["Vui lòng kiểm tra điều kiện khi áp dụng mã."];
    }, [detail]);

    const eligibleRuleCount = useMemo(() => {
        if (!detail?.rules?.length) return 0;
        return detail.rules.filter((rule) => rule.is_eligible === true).length;
    }, [detail]);

    const summaryStatusText = useMemo(() => {
        if (loading) return "Đang kiểm tra điều kiện áp dụng...";
        if (detail?.cart_eligible === true) return "Giỏ hàng hiện tại đã thỏa toàn bộ điều kiện";
        if (detail?.cart_eligible === false) return `Đã thỏa ${eligibleRuleCount}/${detail.rules?.length || 0} điều kiện`;
        return "Đăng nhập để kiểm tra khả dụng theo giỏ hàng";
    }, [detail?.cart_eligible, detail?.rules?.length, eligibleRuleCount, loading]);

    if (!open) return null;

    return (
        <div
            role="presentation"
            style={{
                position: "fixed",
                zIndex: 1300,
                inset: 0,
            }}
        >
            {/* Backdrop */}
            <div
                aria-hidden="true"
                onClick={onClose}
                style={{
                    zIndex: -1,
                    position: "fixed",
                    inset: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    WebkitTapHighlightColor: "transparent",
                }}
            />

            <div tabIndex={0} data-test="sentinelStart" />

            {/* Modal container */}
            <div
                className="styles_guildlinePopup_container__d_YJ_ MuiBox-root css-0"
                tabIndex={-1}
            >
                {/* Header */}
                <div className="MuiBox-root css-0">
                    <div className="styles_guildlinePopup_titleContainer__ToxE8 MuiBox-root css-0">
                        <h6 className="MuiTypography-root MuiTypography-h6">
                            {detail?.name || "Điều kiện đổi điểm"}
                        </h6>

                        <button
                            className="MuiButtonBase-root MuiIconButton-root"
                            type="button"
                            style={{ padding: 0 }}
                            onClick={onClose}
                        >
                            <span className="MuiIconButton-label">
                                <svg
                                    className="MuiSvgIcon-root"
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                    style={{ fill: "rgb(96, 96, 96)" }}
                                >
                                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                                </svg>
                            </span>
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="styles_guildlineStep_container__b3rXY MuiBox-root css-0">
                    {/* Image */}
                    <div className="MuiBox-root css-1vhky2f">
                        <img
                            alt="logo voucher"
                            src="https://gcs.buymed.com/thuocsi-live/images/20255340372e7478c419b066b59f43c9?quality=100"
                            style={{
                                width: "100px",
                                height: "100px",
                                objectFit: "contain",
                            }}
                        />
                    </div>

                    {/* Info */}
                    <div className="styles_contentPopupCondition_wrapper__nkg0V MuiBox-root css-0">
                        

                        {/* Chi tiết ưu đãi */}
                        <div className="MuiGrid-root MuiGrid-container">
                            <div className="MuiGrid-root styles_contentPopupCondition_title__VfIwL MuiGrid-item MuiGrid-grid-xs-12 MuiGrid-grid-md-4">
                                Chi tiết ưu đãi
                            </div>
                            <div className="MuiGrid-root styles_contentPopupCondition_value__LFUMF MuiGrid-item MuiGrid-grid-xs-12 MuiGrid-grid-md-8">
                                {loading ? "Đang tải..." : extractVoucherDetailText(detail)}
                                <div
                                    style={{
                                        marginTop: 8,
                                        padding: "6px 10px",
                                        borderRadius: 8,
                                        display: "inline-block",
                                        background: detail?.cart_eligible === true
                                            ? "#E8FFF1"
                                            : detail?.cart_eligible === false
                                                ? "#FFF7E6"
                                                : "#F3F4F6",
                                        color: detail?.cart_eligible === true
                                            ? "#0E9F6E"
                                            : detail?.cart_eligible === false
                                                ? "#D97706"
                                                : "#6B7280",
                                    }}
                                >
                                    {summaryStatusText}
                                </div>
                                <hr className="MuiDivider-root styles_popupCondition_divider__xARVP" />
                            </div>
                        </div>

                        {/* Điều kiện */}
                        <div className="MuiGrid-root MuiGrid-container">
                            <div className="MuiGrid-root styles_contentPopupCondition_title__VfIwL MuiGrid-item MuiGrid-grid-xs-12 MuiGrid-grid-md-4">
                                Điều kiện áp dụng
                            </div>
                            <div className="MuiGrid-root styles_contentPopupCondition_value__LFUMF MuiGrid-item MuiGrid-grid-xs-12 MuiGrid-grid-md-8">
                                <div>
                                    <h4>{detail?.name || "Khuyến mãi theo hình thức đổi điểm tích luỹ"}</h4>
                                    <ul>
                                        {loading ? (
                                            <li>Đang tải điều kiện...</li>
                                        ) : detail?.rules?.length ? (
                                            detail.rules.map((rule) => (
                                                <li key={`${rule.id}-${rule.rule_type}`}>
                                                    <span>{rule.rule_description || `${rule.rule_type}: ${parseRuleValue(rule.rule_value)}`}</span>{" "}
                                                    <span
                                                        style={{
                                                            fontSize: 12,
                                                            borderRadius: 999,
                                                            padding: "2px 8px",
                                                            backgroundColor: rule.is_eligible === true
                                                                ? "#E8FFF1"
                                                                : rule.is_eligible === false
                                                                    ? "#FFF1F2"
                                                                    : "#F3F4F6",
                                                            color: rule.is_eligible === true
                                                                ? "#0E9F6E"
                                                                : rule.is_eligible === false
                                                                    ? "#E11D48"
                                                                    : "#6B7280",
                                                        }}
                                                    >
                                                        {getRuleStatusLabel(rule.is_eligible)}
                                                    </span>
                                                </li>
                                            ))
                                        ) : (
                                            conditionLines.map((line, index) => (
                                                <li key={`${line}-${index}`}>{line}</li>
                                            ))
                                        )}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer button */}
                <div
                    className="MuiBox-root css-0"
                    style={{ textAlign: "center", marginTop: 12 }}
                >
                    <button
                        className="MuiButtonBase-root MuiButton-root MuiButton-text styles_popupCondition_buyMore___t4FR"
                        type="button"
                        onClick={() => navigate("/quick-order")}
                    >
                        <span className="MuiButton-label">
                            <span>Tích thêm điểm</span>
                        </span>
                    </button>
                </div>
            </div>

            <div tabIndex={0} data-test="sentinelEnd" />
        </div>
    );
};

export default GuidelineModal;
